import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    Mutable
} from "../../../../types";
import type {
    SidebarMenuButtonVariant,
    SidebarActionPlacement,
    SidebarRowMeasures,
    SidebarRowColors,
    SidebarMeasures
} from "../../type";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getRowIconGap
} from "../../../../helpers/theme/control";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const SIDEBAR_MENU_BUTTON_NAME_VARIANT: keyof NCoreUIKit.Typography = "labelMediumSize";

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    textBlock: {
        flexDirection: "column",
        flexShrink: 1,
        minWidth: 0,
        flexGrow: 1
    },
    surface: {
        flexDirection: "row",
        alignItems: "center",
        overflow: "hidden",
        flexGrow: 1
    },
    title: {
        flexShrink: 1
    },
    end: {
        flexShrink: 0
    }
});

export const useStyles = ({
    actionPlacement,
    controlHeights,
    isFocusVisible,
    currentColors,
    currentRadius,
    shapeConfigs,
    isCollapsed,
    rowMeasures,
    avatarSize,
    isDisabled,
    measures,
    borders,
    variant,
    colors,
    spaces
}: {
    spaces: NCoreUIKit.ActivePalette["spaces"];
    colors: NCoreUIKit.ActivePalette["colors"];
    controlHeights: NCoreUIKit.ControlHeights;
    actionPlacement?: SidebarActionPlacement;
    shapeConfigs: NCoreUIKit.ShapeConfigs;
    variant: SidebarMenuButtonVariant;
    rowMeasures: SidebarRowMeasures;
    currentColors: SidebarRowColors;
    borders: NCoreUIKit.Borders;
    measures: SidebarMeasures;
    isFocusVisible: boolean;
    currentRadius: number;
    isCollapsed: boolean;
    isDisabled: boolean;
    avatarSize: number;
}) => {
    const styles = {
        icon: {
            marginStart: (measures.square - avatarSize) / 2 - rowMeasures.inset
        },
        container: {
            minHeight: isCollapsed ? measures.square : rowMeasures.height
        } as Mutable<ViewStyle>,
        surface: {
            backgroundColor: currentColors.containerColor,
            gap: getRowIconGap({
                rowHeight: rowMeasures.height,
                controlHeights,
                spaces
            }),
            paddingStart: rowMeasures.inset,
            paddingEnd: rowMeasures.inset,
            ...getCornerStyle({
                radius: currentRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>
    };

    if(variant === "outline") {
        Object.assign(styles.surface, {
            backgroundColor: isDisabled ? colors.system.state.overlay.disabled.neutral : currentColors.containerColor === "transparent" ? colors.content.container.raised : currentColors.containerColor,
            borderColor: isDisabled ? colors.system.state.border.disabled.neutral : colors.content.border.subtle,
            paddingStart: rowMeasures.inset - borders.line,
            paddingEnd: rowMeasures.inset - borders.line,
            borderWidth: borders.line
        });
    }

    if(isCollapsed) {
        styles.container.width = measures.square;
    }

    if(actionPlacement && !isCollapsed) {
        styles.surface.paddingEnd = actionPlacement.endOffset + actionPlacement.hitSize + spaces.spacingXs - (variant === "outline" ? borders.line : 0);
    }

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        isInset: true,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
